import React, { useCallback } from 'react';
import { hooks, ObservableEvent } from '@lifesize/clients.sdk';
import { injectIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';
import Footer from 'components/InCall/CallFooter';
import actions from 'actions/InCall/InCallVideo';
import { VIDEO_PRIMARY, VIDEO_FOOTER_PIP, VIDEO_PRESENTATION_PIP } from 'constants/InCall/InCallVideo';
import { setTooltipNotification } from 'actions/Notification';
import { tooltipNotifications, tooltipNotificationTypes } from 'constants/Notification';
import { PIP_EXPANDED_SETTING } from 'constants/UserSettings';
import {
  callActions,
  currentUserSelectors,
  streamActions,
  usersActions,
  userProfileSelectors,
  featureSelectors,
  accountSelectors
} from '@lifesize/nucleus';
import _get from 'lodash/get';
import { FLAG_RAISE_HAND } from 'constants/FeatureFlags';

const messages = {
  otherHandRaised: { id: 'notification.tooltip.otherHandRaised', defaultMessage: '{name} has raised their hand' },
  ownHandLoweredByModerator: {
    id: 'notification.tooltip.ownHandLoweredByModerator',
    defaultMessage: 'A moderator has lowered your hand'
  },
  ownHandLoweredBySelf: {
    id: 'notification.tooltip.ownHandLoweredBySelf',
    defaultMessage: 'Your hand has been lowered'
  },
  ownHandLoweredBySelfFailure: {
    id: 'notification.tooltip.ownHandLoweredBySelfFailure',
    defaultMessage: 'An error occurred while lowering your hand',
    messageType: tooltipNotificationTypes.error
  },
  ownHandRaisedBySelf: { id: 'notification.tooltip.ownHandRaisedBySelf', defaultMessage: 'Your hand has been raised' },
  ownHandRaisedBySelfFailure: {
    id: 'notification.tooltip.ownHandRaisedBySelfFailure',
    defaultMessage: 'An error occurred while raising your hand',
    messageType: tooltipNotificationTypes.error
  }
};
/**
 * This container will have management for call controls.
 * Mute, Hangup, local pip display, etc...
 */

const mapActionCreators = (dispatch) => ({
  togglePip: () => {
    dispatch(actions.togglePipExpanded());
  },
  onRaiseHand: (isRaised) => {
    if (!!isRaised) {
      dispatch(usersActions.lowerOwnHand());
    } else {
      dispatch(usersActions.raiseHand());
    }
  },
  onVideoMuteClick: async (mute) => await dispatch(streamActions.toggleVideoMute(mute)),
  onStartRecording: () => dispatch(callActions.startRecording()),
  onStopRecording: () => dispatch(callActions.stopRecording()),
  onKebabClicked: () => dispatch(actions.toggleKebabExpanded()),
  onDTMFClicked: (number) => dispatch(callActions.sendDtmf(number)),
  toggleDialPad: () => {
    dispatch(actions.toggleDialPad());
  }
});

const mapStateToProps = (state, ownProps) => {
  const videoLayout = _get(state, 'app.inCallVideo.videoLayout');
  const videoWidth = _get(videoLayout, ['videos', VIDEO_PRIMARY, 'width']) || _get(videoLayout, 'container.width');
  const btnNotificationRaisedHand =
    _get(state, `app.notification.tooltips.${tooltipNotifications.inCallActionBtnRaiseHand}`) || undefined;
  const showRaiseHandsButton = !!featureSelectors.getFeatureFlag(state)(FLAG_RAISE_HAND);
  const isGuest = userProfileSelectors.selectIsGuest(state);
  const accountSettings = accountSelectors.getAccountSettings(state);
  const isRecording = ownProps.isRecording;
  const recordingSetting = _get(accountSettings, 'recordingEnabled');
  const recordingEnabled = !isGuest && recordingSetting && ownProps.isConnected;
  const canStopRecording = ownProps.canStopRecording;

  return {
    ...ownProps,
    isConnected: ownProps.isConnected,
    startTime: ownProps.call.startTime,
    isVideoMuted: ownProps.mediaState.localVideoMuted,
    btnNotificationRaisedHand,
    canStopRecording,
    isGuest,
    isMinimized: _get(state, 'app.inCallVideo.isMinimized'),
    isRecording,
    kebabExpanded: state.app.inCallVideo.kebabExpanded,
    pipExpanded: _get(currentUserSelectors.myInfo(state), ['userSettings', PIP_EXPANDED_SETTING]),
    recordingEnabled,
    selfPresenting: _get(ownProps, 'selfParticipant.isPresenting', false),
    showCameraControls: _get(state, 'app.inCallVideo.showCameraControls'),
    showDialPad: _get(state, 'app.inCallVideo.showDialPad'),
    videoElementId: VIDEO_FOOTER_PIP,
    videoLayout,
    videoPresentationId: VIDEO_PRESENTATION_PIP,
    videoWidth,
    wasKicked: false, // PT: TODO
    showRaiseHandsButton
  };
};

const ConnectedComponent = injectIntl(connect(mapStateToProps, mapActionCreators)(Footer));

const FunctionalWrapper = (props) => {
  const isConnected = hooks.useCallConnected();
  const inCall = hooks.useInCall();
  const selfParticipant = hooks.useCurrentUserParticipant() || {};
  const { uuid: currentUserUuid } = selfParticipant;
  const isCurrentUserHandRaised = hooks.useIsCurrentUserHandRaised();
  const canRaiseHand = hooks.useCanIssueHandCommand();
  const handIsProcessing = hooks.useIsProcessingHandCommand();
  const mediaState = hooks.useMedia();
  const call = hooks.useCallState();
  const isRecording = hooks.useIsRecordingActive();
  const canStopRecording = hooks.useCanStopRecording();
  const recorder = hooks.useRecordingStartedBy()?.displayName;
  const dispatch = useDispatch();

  const onHandRaised = useCallback(
    (e) => {
      if (currentUserUuid !== _get(e, 'participant.uuid')) {
        dispatch(
          setTooltipNotification(tooltipNotifications.inCallDrawerBtnRaiseHand, {
            ...messages.otherHandRaised,
            values: { name: _get(e, 'participant.name') }
          })
        );
      }
    },
    [dispatch, currentUserUuid]
  );

  const onAllHandsLowered = useCallback(
    (e) => {
      const initiatedByUuid = _get(e, 'initiatedBy.uuid');
      if (currentUserUuid !== initiatedByUuid) {
        dispatch(
          setTooltipNotification(tooltipNotifications.inCallActionBtnRaiseHand, {
            ...messages.ownHandLoweredByModerator,
            values: { name: _get(e, 'initiatedBy.displayName') }
          })
        );
      }
    },
    [dispatch, currentUserUuid]
  );

  const onHandLowered = useCallback(
    (e) => {
      const initiatedByUuid = _get(e, 'initiatedBy.uuid');
      if (currentUserUuid === _get(e, 'participant.uuid') && currentUserUuid !== initiatedByUuid) {
        dispatch(
          setTooltipNotification(tooltipNotifications.inCallActionBtnRaiseHand, {
            ...messages.ownHandLoweredByModerator,
            values: { name: _get(e, 'initiatedBy.displayName') }
          })
        );
      }
    },
    [dispatch, currentUserUuid]
  );

  hooks.useObservableEvent(ObservableEvent.HandRaised, onHandRaised);
  hooks.useObservableEvent(ObservableEvent.AllHandsLowered, onAllHandsLowered);
  hooks.useObservableEvent(ObservableEvent.HandLowered, onHandLowered);

  return (
    <ConnectedComponent
      {...props}
      call={call}
      isConnected={!!isConnected}
      mediaState={mediaState}
      recorder={recorder}
      inCall={inCall}
      selfParticipant={selfParticipant}
      handIsRaised={isCurrentUserHandRaised}
      canRaiseHand={canRaiseHand}
      handIsProcessing={handIsProcessing}
      isRecording={isRecording}
      canStopRecording={canStopRecording}
    />
  );
};

export default FunctionalWrapper;
